import { CollectionsPerPage } from 'constants/browse';
import React, { useEffect, useMemo, useCallback } from 'react';
import {
    Group,
    Text,
    AddToCollectionOutlined,
    IconButton,
} from '@landr/maestro';
import { t, Trans } from '@lingui/macro';
import { UserCollection } from 'types/generated/graphql';
import {
    CollectionsListItem,
    CollectionsListItemSkeleton,
} from 'components/CollectionsListItem';
import { noop } from 'utils/noop';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { css } from '@landr/styled-system/css';
import { StaticItemsCount } from '../CollectionsList';

export const CollectionItemHeight = 60;

export interface CollectionsPageProps {
    fetchMore?: (
        options:
            | {
                  offset: number;
                  query: string;
                  take: number;
              }
            | undefined,
    ) => void;
    hide: () => void;
    items?: UserCollection[];
    loading: boolean;
    pageCount: number;
    pageIndex: number;
    placeholder?: string;
    sampleIds?: string[];
    searchQuery?: string;
    totalItems: number;
}

const createCollectionButtonTextStyles = css({
    width: '232px',
    marginLeft: '3px',
    color: 'text.base',
});

const listItemStyles = css({
    cursor: 'pointer',
    height: `${CollectionItemHeight}px`,
    alignContent: 'center',
    borderRadius: 'base',
    _hover: {
        backgroundColor: 'surface.neutral.medium.hover',
    },
});

export const CollectionsPage: React.FC<CollectionsPageProps> = React.memo(
    ({
        fetchMore = noop,
        hide,
        items = [],
        loading,
        pageCount,
        pageIndex,
        placeholder,
        sampleIds,
        searchQuery,
        totalItems,
    }) => {
        const { openModal } = useModalContext();

        const isFirstPage = pageIndex === 0;
        const isLastPage = pageIndex === pageCount - 1;

        let pageSize = isFirstPage
            ? CollectionsPerPage - StaticItemsCount
            : CollectionsPerPage;

        pageSize = isLastPage
            ? totalItems - (pageIndex * CollectionsPerPage - StaticItemsCount)
            : pageSize;

        const offset = isFirstPage
            ? 0
            : pageIndex * CollectionsPerPage - StaticItemsCount;

        useEffect(() => {
            if (!isFirstPage) {
                fetchMore({
                    variables: {
                        offset,
                        query: searchQuery,
                        take: pageSize,
                    },
                });
            }
            // Should only run when the search query changes
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [searchQuery]);

        const skeletons = useMemo(
            () =>
                new Array(pageSize).fill(true).map((value, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <CollectionsListItemSkeleton key={index} />
                )),
            [pageSize],
        );

        const createNewCollection = useCallback(() => {
            openModal.createCollectionModal();
            hide();
        }, [openModal, hide]);

        const createCollectionButton = useMemo(
            () => (
                <Group
                    alignItems="center"
                    gap="sm"
                    py="sm"
                    width="100%"
                    onClick={createNewCollection}
                >
                    <Group.Item>
                        <IconButton
                            variant="subtle"
                            label={t`Create new collection`}
                            size="sm"
                            marginLeft="xs"
                            icon={AddToCollectionOutlined}
                            isRounded
                        />
                    </Group.Item>
                    <Group.Item>
                        <Text
                            textAlign="left"
                            fontWeight="bold"
                            className={createCollectionButtonTextStyles}
                        >
                            <Trans>Create new collection</Trans>
                        </Text>
                    </Group.Item>
                </Group>
            ),
            [createNewCollection],
        );

        const renderedItems = items.slice(offset, offset + pageSize);

        if (loading) {
            return (
                <>
                    {isFirstPage && createCollectionButton}
                    {skeletons}
                </>
            );
        }

        return (
            <Group direction="vertical" gap="none">
                {isFirstPage && (
                    <Group.Item className={listItemStyles}>
                        {createCollectionButton}
                    </Group.Item>
                )}
                {Object.values(renderedItems).map((item) => (
                    <Group.Item key={item.guid} className={listItemStyles}>
                        <CollectionsListItem
                            item={item}
                            sampleIds={sampleIds}
                            hide={hide}
                        />
                    </Group.Item>
                ))}
            </Group>
        );
    },
);

export default CollectionsPage;
